import { type ReactNode, useEffect, useRef } from 'react'
import cx from 'classnames'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { Icon } from '@/components/Icon'
import { Tooltip } from '@/components/Tooltip'
import { IconButton } from '@/components/IconButton'
import { Button } from '@/components/Button'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
} from '@/components/ModalSheet'
import styles from './JobNotesCard.module.css'

export interface JobNotesCardProps {
  isHighlighted: boolean
  canWrite: boolean
  onAddNote: () => unknown
  isMovedNoticeShowing: boolean
  onCloseMovedNotice: () => unknown
  deleteConfirmSheet: ReactNode
  children: ReactNode
}

export function JobNotesCard({
  isHighlighted,
  canWrite,
  onAddNote,
  isMovedNoticeShowing,
  onCloseMovedNotice,
  deleteConfirmSheet,
  children,
}: JobNotesCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isHighlighted) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [isHighlighted])

  return (
    <>
      <div
        ref={cardRef}
        className={cx(styles.cardWrap, isHighlighted && styles.highlighted)}
      >
        <Card>
          <CardHeader bordered compact>
            <div className={styles.cardHeader}>
              <div className={styles.titleGroup}>
                <CardTitle testId="job-notes-title">Job notes</CardTitle>
                <Tooltip content="Notes can be seen by anyone with access to the job, minus the client.">
                  <span className={styles.info}>
                    <Icon name="info" />
                  </span>
                </Tooltip>
              </div>
              {canWrite && (
                <IconButton
                  name="add"
                  label="Add note"
                  onPress={onAddNote}
                  testId="job-notes-add"
                />
              )}
            </div>
          </CardHeader>

          <CardContent listContent>{children}</CardContent>
        </Card>
      </div>

      <ModalSheet
        showing={isMovedNoticeShowing}
        onRequestClose={onCloseMovedNotice}
      >
        <ModalSheetHeader withCloseButton />
        <ModalSheetContent>
          <ModalSheetContentBlock>
            <ModalSheetContentTitle>
              Notes now have their own section
            </ModalSheetContentTitle>
            <ModalSheetContentDescription>
              Your notes live in this card now. Anyone with access to the job
              can see them here, excluding the client.
            </ModalSheetContentDescription>
          </ModalSheetContentBlock>
        </ModalSheetContent>
        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button
              label="Got it!"
              variant="primary"
              onPress={onCloseMovedNotice}
            />
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>

      {deleteConfirmSheet}
    </>
  )
}

export interface JobNotesCardListProps {
  children: ReactNode
}

export function JobNotesCardList({ children }: JobNotesCardListProps) {
  return <div className={styles.list}>{children}</div>
}

export function JobNotesDeleteConfirmContent() {
  return (
    <ModalSheetContentBlock>
      <ModalSheetContentTitle>Delete this note?</ModalSheetContentTitle>
      <ModalSheetContentDescription>
        This note will be removed for your whole team. This can't be undone.
      </ModalSheetContentDescription>
    </ModalSheetContentBlock>
  )
}
